import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { Link } from "react-router-dom";
import { UsersRound, Loader2, Check, X, Heart, Users, Home, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const LABEL_META = {
  friend: { label: "Friend", icon: Heart },
  connect: { label: "Connect", icon: Users },
  family: { label: "Family", icon: Home },
};

export function KindCircle() {
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/circle"); setData(data); }
    catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (connection_id, accept, label = "connect") => {
    try {
      await api.post("/circle/respond", { connection_id, accept, label });
      toast.success(accept ? "Added to your Kind Circle 🤝" : "Request declined");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="rud-card rounded-2xl p-6 mt-4"><HeartLoader /></div>;
  if (!data) return null;
  const { circle = [], incoming = [], outgoing = [], counts = {} } = data;

  return (
    <div className="rud-card rounded-2xl p-6 mt-4" data-testid="kind-circle">
      <div className="flex items-center gap-2 mb-1"><UsersRound size={18} className="text-brand" /><h3 className="font-serif text-2xl">Kind Circle</h3></div>
      <p className="text-xs text-sec mb-4">Follow anyone one-way, or send a Connect request (Friend · Connect · Family). Both of you must agree to be in each other's Kind Circle.</p>

      {(() => {
        const vs = Math.max(0.1, Math.min(1, Number(user?.visibility_score ?? 1)));
        const pct = Math.round(vs * 100);
        const tone = vs >= 0.85 ? "text-brand" : vs >= 0.5 ? "text-amber-400" : "text-rose-400";
        const msg = vs >= 0.85 ? "Glowing bright — thank you for keeping it kind 🙏" : vs >= 0.5 ? "Good — a little more kindness lifts your reach." : "Your reach is dimmed by unkind content. Kindness restores it.";
        return (
          <div className="rounded-xl border border-brand/25 bg-brand/5 p-4 mb-5" data-testid="kindness-badge">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium"><Sparkles size={15} className="text-brand" /> Kindness &amp; Reach</div>
              <div className="flex items-center gap-2">
                {vs >= 0.9 && <span data-testid="kind-soul-badge" className="inline-flex items-center gap-1 text-[11px] text-brand bg-brand/15 rounded-full px-2 py-0.5"><Sparkles size={11} /> Kind Soul</span>}
                <div className={`text-lg font-serif ${tone}`} data-testid="kindness-score">{pct}%</div>
              </div>
            </div>
            <div className="h-2 rounded-full bg-white/10 mt-2 overflow-hidden">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%`, boxShadow: vs >= 0.85 ? "0 0 12px var(--rud-brand)" : "none" }} />
            </div>
            <p className="text-[11px] text-sec mt-1.5">{msg}</p>
          </div>
        );
      })()}

      <div className="grid grid-cols-3 gap-2 mb-5">
        {[["friend", counts.friend || 0], ["connect", counts.connect || 0], ["family", counts.family || 0]].map(([k, n]) => {
          const M = LABEL_META[k]; const Icon = M.icon;
          return <div key={k} className="rounded-xl border border-rud bg-white/5 p-3 text-center" data-testid={`circle-count-${k}`}>
            <Icon size={16} className="mx-auto text-brand" /><div className="text-lg font-serif mt-1">{n}</div><div className="text-[11px] text-sec">{k === "family" ? "Family" : M.label + "s"}</div>
          </div>;
        })}
      </div>
      <div className="flex gap-4 text-xs text-sec mb-5">
        <span><b className="text-[var(--rud-text)]">{counts.following || 0}</b> Following</span>
        <span><b className="text-[var(--rud-text)]">{counts.followers || 0}</b> Followers</span>
      </div>

      {incoming.length > 0 && (
        <div className="mb-5" data-testid="circle-incoming">
          <div className="text-sm font-medium mb-2">Connect requests ({incoming.length})</div>
          <div className="space-y-2">
            {incoming.map((r) => (
              <div key={r.connection_id} data-testid={`circle-request-${r.user_id}`} className="flex items-center gap-2 rounded-lg border border-rud bg-white/5 px-3 py-2">
                <div className="flex-1 min-w-0 text-sm truncate">{r.name} <span className="text-[11px] text-brand">wants to be your {LABEL_META[r.label]?.label || "Connect"}</span></div>
                <button data-testid={`circle-accept-${r.user_id}`} onClick={() => respond(r.connection_id, true, r.label)} className="btn-primary rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><Check size={13} /> Accept</button>
                <button data-testid={`circle-decline-${r.user_id}`} onClick={() => respond(r.connection_id, false)} className="text-sec hover:text-rose-400"><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-sm font-medium mb-2">My Kind Circle</div>
      {circle.length === 0 ? (
        <p className="text-sm text-sec">No connections yet. Follow people and send Connect requests to grow your Kind Circle. 💛</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="circle-list">
          {circle.map((c) => {
            const M = LABEL_META[c.label] || LABEL_META.connect; const Icon = M.icon;
            return (
              <div key={c.user_id} className="flex items-center gap-3 rounded-lg border border-rud bg-white/5 px-3 py-2.5">
                <div className="w-8 h-8 rounded-full bg-brand/15 text-brand grid place-items-center text-xs font-bold shrink-0">{(c.name || "?")[0]?.toUpperCase()}</div>
                <Link to={`/u/${c.user_id}`} className="flex-1 min-w-0 text-sm truncate hover:text-brand transition-colors">{c.name}</Link>
                <span className="text-[11px] text-brand flex items-center gap-1 shrink-0"><Icon size={12} /> {M.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {outgoing.length > 0 && (
        <p className="text-[11px] text-sec mt-3" data-testid="circle-outgoing">{outgoing.length} pending request{outgoing.length === 1 ? "" : "s"} you sent, awaiting approval.</p>
      )}
    </div>
  );
}

export default KindCircle;
